import React, { useEffect, useRef } from 'react';
import { Link } from 'react-router-dom';
import {
  ArrowRight,
  Users,
  Target,
  RefreshCw,
  Zap,
  CheckCircle,
  TrendingUp,
  Shield,
} from 'lucide-react';
import SEO from '../components/SEO';

const Home = () => {
  const sectionsRef = useRef([]);

  useEffect(() => {
    const observer = new IntersectionObserver(
      (entries) => {
        entries.forEach((entry) => {
          if (entry.isIntersecting) {
            entry.target.classList.add('animate-fadeIn');
          }
        });
      },
      { threshold: 0.1 }
    );

    sectionsRef.current.forEach((section) => {
      if (section) observer.observe(section);
    });

    return () => observer.disconnect();
  }, []);

  const services = [
    {
      icon: Users,
      title: 'IT Staff Augmentation',
      description: 'Scale your team quickly with experienced developers, engineers, and analysts.',
      color: 'from-blue-500 to-cyan-500',
      link: '/services/staff-augmentation',
    },
    {
      icon: Target,
      title: 'Permanent Recruitment',
      description: 'Find full-time tech professionals who match your culture and technical needs.',
      color: 'from-purple-500 to-pink-500',
      link: '/services/permanent-recruitment',
    },
    {
      icon: RefreshCw,
      title: 'Contract-to-Hire',
      description: 'Test-fit employees before making a long-term commitment to your team.',
      color: 'from-orange-500 to-yellow-500',
      link: '/services/contract-to-hire',
    },
  ];

  const stats = [
    { value: '500+', label: 'Placements' },
    { value: '98%', label: 'Success Rate' },
    { value: '50+', label: 'Enterprise Clients' },
    { value: '24h', label: 'Response Time' },
  ];

  const clients = [
    { name: 'Infosys', logo: 'https://upload.wikimedia.org/wikipedia/commons/9/95/Infosys_logo.svg' },
    { name: 'National Australia Bank', logo: '/nab-logo.svg' },
    { name: 'American Express', logo: 'https://upload.wikimedia.org/wikipedia/commons/f/fa/American_Express_logo_%282018%29.svg' },
    { name: 'ANZ', logo: '/anz-logo.svg' },
    { name: 'Cisco', logo: 'https://upload.wikimedia.org/wikipedia/commons/0/08/Cisco_logo_blue_2016.svg' },
  ];

  const features = [
    {
      icon: Zap,
      title: 'Fast Hiring',
      description: 'Quick turnaround time for urgent staffing needs with our pre-vetted talent pool.',
    },
    {
      icon: Shield,
      title: 'Vetted Talent',
      description: 'Every candidate undergoes rigorous technical and cultural assessments.',
    },
    {
      icon: TrendingUp,
      title: 'Industry Expertise',
      description: 'Deep knowledge across finance, healthcare, e-commerce, and more.',
    },
    {
      icon: CheckCircle,
      title: 'End-to-End Support',
      description: 'From sourcing to onboarding, we manage the entire recruitment process.',
    },
  ];

  return (
    <div className="min-h-screen bg-white">
      <SEO
        title="IT Staffing & Consulting Services"
        description="Leading IT staffing and consulting firm in Hyderabad. We connect businesses with top-tier technology talent through staff augmentation, permanent recruitment, and contract-to-hire solutions."
        keywords="IT staffing, IT recruitment, staff augmentation, IT consulting, hire developers, IT contractors, Hyderabad"
      />

      {/* Hero Section */}
      <section className="relative pt-32 pb-20 px-6 overflow-hidden">
        {/* Gradient Background */}
        <div className="absolute inset-0 bg-gradient-to-br from-blue-50 via-purple-50 to-orange-50 opacity-60"></div>
        <div className="absolute top-0 right-0 w-[600px] h-[600px] bg-gradient-to-br from-purple-400/20 via-pink-400/20 to-orange-400/20 rounded-full blur-3xl"></div>
        <div className="absolute bottom-0 left-0 w-[400px] h-[400px] bg-gradient-to-tr from-blue-400/20 via-cyan-400/20 to-purple-400/20 rounded-full blur-3xl"></div>
        
        <div className="max-w-7xl mx-auto relative z-10">
          <div className="max-w-4xl">
            <div className="inline-block mb-6 px-4 py-2 bg-white/80 backdrop-blur-sm rounded-full border border-slate-200">
              <span className="text-sm font-medium text-slate-700">
                Trusted by 50+ enterprise companies
              </span>
            </div>
            <h1 className="text-5xl md:text-7xl font-bold text-slate-900 mb-6 leading-tight">
              IT talent infrastructure to{' '}
              <span className="bg-gradient-to-r from-blue-600 via-purple-600 to-orange-500 bg-clip-text text-transparent">
                grow your business
              </span>
            </h1>
            <p className="text-xl md:text-2xl text-slate-600 mb-8 leading-relaxed max-w-3xl">
              We specialize in sourcing, vetting, and placing skilled IT professionals tailored to
              your organization's needs—from your first hire to your hundredth.
            </p>
            <div className="flex flex-col sm:flex-row gap-4">
              <Link
                to="/contact-us"
                className="inline-flex items-center justify-center space-x-2 px-8 py-4 bg-[#f9a825] text-white rounded-full hover:bg-[#f57c00] transition-all duration-200 font-semibold text-lg group"
              >
                <span>Get Started</span>
                <ArrowRight className="w-5 h-5 group-hover:translate-x-1 transition-transform duration-200" />
              </Link>
              <Link
                to="/services"
                className="inline-flex items-center justify-center space-x-2 px-8 py-4 bg-white text-slate-900 rounded-full border-2 border-slate-200 hover:border-slate-300 transition-all duration-200 font-semibold text-lg"
              >
                <span>View Services</span>
              </Link>
            </div>
          </div>

          {/* Stats */}
          <div className="grid grid-cols-2 md:grid-cols-4 gap-8 mt-20">
            {stats.map((stat, index) => (
              <div key={index} className="text-center">
                <div className="text-4xl md:text-5xl font-bold text-slate-900 mb-2">{stat.value}</div>
                <div className="text-sm text-slate-600 font-medium">{stat.label}</div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Trusted By Section */}
      <section
        ref={(el) => (sectionsRef.current[0] = el)}
        className="py-16 px-6 bg-white opacity-0 transition-opacity duration-1000"
      >
        <div className="max-w-7xl mx-auto">
          <p className="text-center text-sm font-semibold text-slate-500 uppercase tracking-wider mb-8">
            Trusted by industry leaders
          </p>
          <div className="grid grid-cols-2 md:grid-cols-5 gap-8 items-center">
            {clients.map((client, index) => (
              <div key={index} className="flex items-center justify-center grayscale hover:grayscale-0 transition-all duration-300 opacity-60 hover:opacity-100">
                <img src={client.logo} alt={client.name} className="h-8 w-auto" />
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Services Section */}
      <section
        ref={(el) => (sectionsRef.current[1] = el)}
        className="py-24 px-6 bg-slate-50 opacity-0 transition-opacity duration-1000"
      >
        <div className="max-w-7xl mx-auto">
          <div className="text-center mb-16">
            <h2 className="text-4xl md:text-5xl font-bold text-slate-900 mb-4">
              Flexible solutions for every business model
            </h2>
            <p className="text-xl text-slate-600 max-w-3xl mx-auto">
              Grow your business with a comprehensive set of staffing and recruitment tools—designed to work individually or together.
            </p>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-3 gap-8">
            {services.map((service, index) => {
              const Icon = service.icon;
              return (
                <Link
                  key={index}
                  to={service.link}
                  className="group bg-white rounded-2xl p-8 hover:shadow-xl transition-all duration-300 border border-slate-200 hover:border-slate-300"
                >
                  <div className={`w-12 h-12 rounded-xl bg-gradient-to-br ${service.color} flex items-center justify-center mb-6 group-hover:scale-110 transition-transform duration-300`}>
                    <Icon className="w-6 h-6 text-white" />
                  </div>
                  <h3 className="text-xl font-semibold text-slate-900 mb-3">{service.title}</h3>
                  <p className="text-slate-600 mb-4 leading-relaxed">{service.description}</p>
                  <div className="flex items-center text-[#f9a825] font-medium group-hover:translate-x-2 transition-transform duration-200">
                    <span className="text-sm">Learn more</span>
                    <ArrowRight className="w-4 h-4 ml-2" />
                  </div>
                </Link>
              );
            })}
          </div>
        </div>
      </section>

      {/* Features Grid */}
      <section
        ref={(el) => (sectionsRef.current[2] = el)}
        className="py-24 px-6 bg-white opacity-0 transition-opacity duration-1000"
      >
        <div className="max-w-7xl mx-auto">
          <div className="text-center mb-16">
            <h2 className="text-4xl md:text-5xl font-bold text-slate-900 mb-4">
              Why leading companies choose us
            </h2>
            <p className="text-xl text-slate-600 max-w-3xl mx-auto">
              We offer fast and flexible hiring solutions backed by a deep network of pre-vetted IT professionals.
            </p>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 gap-8">
            {features.map((feature, index) => {
              const Icon = feature.icon;
              return (
                <div
                  key={index}
                  className="bg-slate-50 rounded-2xl p-8 hover:bg-slate-100 transition-all duration-300"
                >
                  <Icon className="w-10 h-10 text-[#f9a825] mb-4" />
                  <h3 className="text-xl font-semibold text-slate-900 mb-2">{feature.title}</h3>
                  <p className="text-slate-600 leading-relaxed">{feature.description}</p>
                </div>
              );
            })}
          </div>
        </div>
      </section>

      {/* CTA Section */}
      <section
        ref={(el) => (sectionsRef.current[3] = el)}
        className="py-24 px-6 bg-gradient-to-br from-slate-900 via-slate-800 to-slate-900 opacity-0 transition-opacity duration-1000 relative overflow-hidden"
      >
        {/* Decorative gradient */}
        <div className="absolute inset-0 bg-gradient-to-br from-purple-500/10 via-transparent to-orange-500/10"></div>
        
        <div className="max-w-4xl mx-auto text-center relative z-10">
          <h2 className="text-4xl md:text-5xl font-bold text-white mb-6">
            Ready to build your dream team?
          </h2>
          <p className="text-xl text-slate-300 mb-8">
            Let's discuss how we can help you find the perfect IT talent for your organization.
          </p>
          <div className="flex flex-col sm:flex-row gap-4 justify-center">
            <Link
              to="/contact-us"
              className="inline-flex items-center justify-center space-x-2 px-8 py-4 bg-[#f9a825] text-white rounded-full hover:bg-[#f57c00] transition-all duration-200 font-semibold text-lg group"
            >
              <span>Get Started</span>
              <ArrowRight className="w-5 h-5 group-hover:translate-x-1 transition-transform duration-200" />
            </Link>
            <a
              href="tel:+91-9121711433"
              className="inline-flex items-center justify-center space-x-2 px-8 py-4 bg-white/10 backdrop-blur-sm text-white rounded-full border-2 border-white/20 hover:bg-white/20 transition-all duration-200 font-semibold text-lg"
            >
              <span>Call: +91-9121711433</span>
            </a>
          </div>
        </div>
      </section>
    </div>
  );
};

export default Home;
